import React from 'react';
import { CloudOff, WifiOff } from 'lucide-react';
import { useSyncStatus } from '../../hooks/useSyncStatus';
import { useOnlineStatus } from '../../hooks/useOfflineDevice';

export interface OfflineBannerProps {
  onOpenSyncCenter: () => void;
}

/**
 * Field-mode banner (Phase 16C C8): offline state and "N items not synced", on every screen of an
 * enrolled, unlocked phone or tablet. Hidden when online with nothing pending.
 */
const OfflineBanner: React.FC<OfflineBannerProps> = ({ onOpenSyncCenter }) => {
  const online = useOnlineStatus();
  const status = useSyncStatus();
  if (!status.active || (online && status.pending === 0)) return null;
  const problems = status.conflicts + status.attention;
  return (
    <div role="status" aria-live="polite" className={`flex min-h-[44px] items-center gap-2 px-4 text-sm ${problems ? 'bg-red-700 text-white' : online ? 'bg-amber-100 text-amber-950' : 'bg-slate-800 text-white'}`}>
      {online ? <CloudOff size={16} aria-hidden="true" /> : <WifiOff size={16} aria-hidden="true" />}
      <span className="min-w-0 flex-1 truncate">
        {online ? '' : 'Offline · '}
        {status.pending === 0 ? 'work is saved on this device' : `${status.pending} item${status.pending === 1 ? '' : 's'} not synced${problems ? ` · ${problems} need${problems === 1 ? 's' : ''} attention` : ''}`}
      </span>
      {status.pending > 0 && (
        <button type="button" onClick={onOpenSyncCenter} className="min-h-[40px] shrink-0 rounded-md border border-current px-3 text-sm font-semibold">Sync Center</button>
      )}
    </div>
  );
};

export default OfflineBanner;
